import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAbpPermissions } from '../../contexts/AbpPermissionsContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../../components/Layout';
import { BookListFilters } from '../../components/admin/books/BookListFilters';
import { BookAdvancedFilters } from '../../components/admin/books/BookAdvancedFilters';
import { BookSavedViews } from '../../components/admin/books/BookSavedViews';
import { BookListTable } from '../../components/admin/books/BookListTable';
import { BookBulkActionsBar } from '../../components/admin/books/BookBulkActionsBar';
import { BookDeleteConfirmDialog } from '../../components/admin/books/BookDeleteConfirmDialog';
import { BookStatusChangeDialog } from '../../components/admin/books/BookStatusChangeDialog';
import { BookQuotaEditDialog } from '../../components/admin/books/BookQuotaEditDialog';
import type { QuotaDialogResult } from '../../components/admin/books/BookQuotaEditDialog';
import { BookEditDrawer } from '../../components/admin/books/BookEditDrawer';
import { useBookListQuery } from '../../hooks/useBookListQuery';
import { useBulkBookActions } from '../../hooks/useBulkBookActions';
import { booksManageService } from '../../api/booksManageService';
import { getActivePublishers } from '../../api/publishers';
import {
  DEFAULT_FILTER_STATE,
  type BookFilterState,
  type BookStatus,
  type BulkOperationResultDto,
} from '../../types/manageBooks';
import type { BookDto } from '../../types/book';
import type { PublisherDto } from '../../types/publisher';
import { useTranslation } from '../../i18n/useTranslation';

function parseNumber(v: string | null): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function parseBool(v: string | null): boolean | undefined {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return undefined;
}

function filtersFromParams(params: URLSearchParams): BookFilterState {
  return {
    q: params.get('q') ?? undefined,
    status: parseNumber(params.get('status')) as BookFilterState['status'],
    format: parseNumber(params.get('format')) as BookFilterState['format'],
    publisherId: params.get('publisherId') ?? undefined,
    drm: parseBool(params.get('drm')),
    rendererType: parseNumber(params.get('rendererType')) as BookFilterState['rendererType'],
    complexityMin: parseNumber(params.get('complexityMin')),
    complexityMax: parseNumber(params.get('complexityMax')),
    createdAfter: params.get('createdAfter') ?? undefined,
    createdBefore: params.get('createdBefore') ?? undefined,
    hasCover: parseBool(params.get('hasCover')),
    includeDeleted: parseBool(params.get('includeDeleted')),
    includeArchived: parseBool(params.get('includeArchived')),
    sort: params.get('sort') ?? DEFAULT_FILTER_STATE.sort,
    dir: (params.get('dir') as 'asc' | 'desc' | null) ?? DEFAULT_FILTER_STATE.dir,
    page: parseNumber(params.get('page')) ?? 1,
    pageSize: parseNumber(params.get('pageSize')) ?? 25,
  };
}

function paramsFromFilters(f: BookFilterState): Record<string, string> {
  const out: Record<string, string> = {};
  const entries = Object.entries(f) as [keyof BookFilterState, unknown][];
  for (const [k, v] of entries) {
    if (v === undefined || v === null || v === '') continue;
    out[k as string] = String(v);
  }
  return out;
}

export default function ManageBooksPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const editId = searchParams.get('edit');
  const qc = useQueryClient();

  const { hasPermission } = useAbpPermissions();
  const canSwitchRenderer = hasPermission('learnleaf.App.SwitchBookRenderer');
  const [pendingSwitchBookId, setPendingSwitchBookId] = useState<string | null>(null);

  const [selection, setSelection] = useState<Map<string, BookDto>>(new Map());
  const [dialog, setDialog] = useState<'none' | 'delete' | 'status' | 'quota'>('none');
  const [lastResult, setLastResult] = useState<BulkOperationResultDto | null>(null);

  const { data: publishersRaw } = useQuery<PublisherDto[]>({
    queryKey: ['publishers', 'active'],
    queryFn: getActivePublishers,
    staleTime: 5 * 60 * 1000,
  });
  const publishers = useMemo(
    () => (publishersRaw ?? []).map(p => ({ id: p.id, name: p.name })),
    [publishersRaw]
  );

  const list = useBookListQuery(filters);
  const bulk = useBulkBookActions();

  const bulkBusy = bulk.bulkDelete.isPending || bulk.bulkStatus.isPending || bulk.bulkQuotas.isPending;

  const applyFilters = (next: BookFilterState) => {
    // Detect a "meaningful" filter change (anything except page / edit).
    const changedField = (() => {
      const keys: (keyof BookFilterState)[] = [
        'q', 'status', 'format', 'publisherId', 'drm', 'rendererType',
        'complexityMin', 'complexityMax', 'createdAfter', 'createdBefore',
        'hasCover', 'includeDeleted', 'includeArchived', 'sort', 'dir', 'pageSize',
      ];
      return keys.some(k => next[k] !== filters[k]);
    })();

    if (changedField && selection.size > 0) {
      const ok = window.confirm(
        t('App.Admin.Books.ManageBooksPage.FilterClearConfirm', { count: selection.size })
      );
      if (!ok) return;
      setSelection(new Map());
    }

    const merged: Record<string, string> = paramsFromFilters(next);
    if (editId) merged.edit = editId;
    setSearchParams(merged);
  };

  const openEdit = (book: BookDto) => {
    const next = { ...paramsFromFilters(filters), edit: book.id };
    setSearchParams(next);
  };
  const closeEdit = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next);
  };

  const toggleRow = (b: BookDto) => {
    setSelection(prev => {
      const m = new Map(prev);
      if (m.has(b.id)) m.delete(b.id); else m.set(b.id, b);
      return m;
    });
  };

  const togglePage = (all: boolean) => {
    const rows = list.data?.items ?? [];
    setSelection(prev => {
      const m = new Map(prev);
      for (const b of rows) {
        if (all) m.set(b.id, b); else m.delete(b.id);
      }
      return m;
    });
  };

  const selectAllFilter = async () => {
    try {
      const ids = await booksManageService.idsForFilter(filters);
      if (ids.length === 0) return;
      if (ids.length >= 500) {
        const proceed = window.confirm(
          t('App.Admin.Books.ManageBooksPage.SelectAllMatchedCap', { count: ids.length })
        );
        if (!proceed) return;
      }
      const rowIndex = new Map((list.data?.items ?? []).map(b => [b.id, b]));
      setSelection(prev => {
        const m = new Map(prev);
        for (const id of ids) {
          const known = rowIndex.get(id);
          m.set(id, known ?? ({ id } as BookDto));
        }
        return m;
      });
    } catch (err) {
      window.alert(t('App.Admin.Books.ManageBooksPage.SelectAllMatchedFailed', { error: err instanceof Error ? err.message : String(err) }));
    }
  };

  const runBulkDelete = async () => {
    const ids = [...selection.keys()];
    if (ids.length === 0) return;
    const res = await bulk.bulkDelete.mutateAsync(ids);
    setLastResult(res);
    setSelection(new Map());
    setDialog('none');
  };

  const runBulkStatus = async (status: BookStatus) => {
    const ids = [...selection.keys()];
    if (ids.length === 0) return;
    const res = await bulk.bulkStatus.mutateAsync({ bookIds: ids, status });
    setLastResult(res);
    setSelection(new Map());
    setDialog('none');
  };

  const runBulkQuotas = async (payload: QuotaDialogResult) => {
    const ids = [...selection.keys()];
    if (ids.length === 0) return;
    const res = await bulk.bulkQuotas.mutateAsync({ bookIds: ids, ...payload });
    setLastResult(res);
    setSelection(new Map());
    setDialog('none');
  };

  const exportExcel = async () => {
    try {
      const blob = await booksManageService.exportExcel(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `books-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(t('App.Admin.Books.ManageBooksPage.ExportFailed', { error: err instanceof Error ? err.message : String(err) }));
    }
  };

  const onSaved = () => {
    void qc.invalidateQueries({ queryKey: ['manage-books'] });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-bold">{t('App.Admin.Books.ManageBooksPage.Title')}</h1>
          <BookSavedViews
            currentFilters={filters}
            onLoad={(f) => setSearchParams(paramsFromFilters(f))}
          />
        </div>

        <BookListFilters filters={filters} publishers={publishers} onChange={applyFilters} />
        <BookAdvancedFilters filters={filters} onChange={applyFilters} />

        <BookBulkActionsBar
          selectedCount={selection.size}
          onDelete={() => setDialog('delete')}
          onStatus={() => setDialog('status')}
          onQuotas={() => setDialog('quota')}
          onExport={exportExcel}
          onClearSelection={() => setSelection(new Map())}
          onSelectAllFilter={selectAllFilter}
          busy={bulkBusy}
        />

        {lastResult && (
          <div
            role="status" aria-live="polite"
            className="border rounded p-2 text-sm bg-neutral-50"
          >
            {t('App.Admin.Books.ManageBooksPage.ResultSummary', { successCount: lastResult.successCount, failureCount: lastResult.failureCount })}
            {lastResult.failures.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer">{t('App.Admin.Books.ManageBooksPage.ShowFailures')}</summary>
                <ul className="mt-1 pl-5 list-disc">
                  {lastResult.failures.map(f => (
                    <li key={f.bookId}>
                      <span className="font-mono text-xs">{f.bookId.slice(0, 8)}</span>: {f.errorMessage || t('App.Common.Unknown')}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <button
              type="button"
              onClick={() => setLastResult(null)}
              className="ml-2 text-primary-600 text-xs"
            >
              {t('App.Admin.Books.ManageBooksPage.Dismiss')}
            </button>
          </div>
        )}

        <BookListTable
          rows={list.data?.items ?? []}
          totalCount={list.data?.totalCount ?? 0}
          loading={list.isLoading || list.isFetching}
          filters={filters}
          selection={selection}
          onToggleRow={toggleRow}
          onTogglePage={togglePage}
          onSortChange={(col) => applyFilters({
            ...filters,
            sort: col,
            dir: filters.sort === col && filters.dir === 'asc' ? 'desc' : 'asc',
          })}
          onPageChange={(p) => applyFilters({ ...filters, page: p })}
          onRowClick={openEdit}
          canSwitchRenderer={canSwitchRenderer}
          pendingSwitchBookId={pendingSwitchBookId}
          onSwitchDialogOpened={() => setPendingSwitchBookId(null)}
        />

        <BookDeleteConfirmDialog
          open={dialog === 'delete'}
          count={selection.size}
          onCancel={() => setDialog('none')}
          onConfirm={runBulkDelete}
          busy={bulk.bulkDelete.isPending}
        />
        <BookStatusChangeDialog
          open={dialog === 'status'}
          count={selection.size}
          onCancel={() => setDialog('none')}
          onConfirm={runBulkStatus}
          busy={bulk.bulkStatus.isPending}
        />
        <BookQuotaEditDialog
          open={dialog === 'quota'}
          count={selection.size}
          onCancel={() => setDialog('none')}
          onConfirm={runBulkQuotas}
          busy={bulk.bulkQuotas.isPending}
        />

        <BookEditDrawer
          bookId={editId}
          publishers={publishers}
          onClose={closeEdit}
          onSaved={onSaved}
          canSwitchRenderer={canSwitchRenderer}
          onRequestRendererSwitch={(bookId) => {
            closeEdit();
            setPendingSwitchBookId(bookId);
          }}
        />
      </div>
    </Layout>
  );
}
